import { useRef, useState } from "react";
import { motion, useReducedMotion, useScroll, useMotionValueEvent } from "framer-motion";
import { ArrowRight, ShieldCheck, Sparkles, Accessibility } from "lucide-react";
import { RevealLines, Reveal } from "@/lib/reveal";
import { DIMENSIONS } from "@/data/content";
import { handleAnchorClick } from "@/lib/scroll";
import { AidCard } from "@/components/AidCard";

const HeroIdentityCard = ({ activeDim = 0 }) => {
  const reduce = useReducedMotion();
  const radius = 158;
  return (
    <div className="relative w-full max-w-[560px] mx-auto aspect-square" aria-hidden="true">
      {/* ambient glow */}
      <div className="absolute inset-0 radial-glow scale-125" />
      {/* rings */}
      <div className="absolute inset-8 rounded-full border border-teal/20" />
      <div className="absolute inset-24 rounded-full border border-cyan/15" />
      <div className="absolute inset-[150px] rounded-full border border-white/10" />

      {/* orbiting dimension nodes */}
      {DIMENSIONS.map((d, i) => {
        const angle = (i / DIMENSIONS.length) * Math.PI * 2 - Math.PI / 2;
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;
        return (
          <div
            key={d.label}
            className="absolute left-1/2 top-1/2 transition-all duration-500"
            style={{ transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) scale(${i === activeDim ? 1.16 : 1})`, opacity: i === activeDim ? 1 : 0.45 }}
          >
            {i === activeDim && !reduce && (
              <span className="absolute inset-0 rounded-2xl animate-pulse-ring" style={{ background: `${d.color}55` }} />
            )}
            <div
              className="relative flex h-14 w-14 items-center justify-center rounded-2xl glass transition-all duration-500"
              style={{
                boxShadow: i === activeDim ? `0 0 34px -4px ${d.color}` : "none",
                borderColor: i === activeDim ? d.color : "rgba(255,255,255,0.08)",
              }}
            >
              <d.Icon className="h-7 w-7 transition-colors duration-500" style={{ color: i === activeDim ? d.color : "#94A3B8" }} aria-hidden="true" />
            </div>
          </div>
        );
      })}

      {/* central node — the wrapper handles centring; the motion element only
          floats it. (Framer Motion writes an inline transform for `y`, which
          would otherwise override the -translate-x/y-1/2 centring classes and
          push the card down-and-right of the rings' centre.) */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <motion.div
          animate={reduce ? {} : { y: [0, -8, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        >
          <div className="relative">
            <span className="absolute inset-0 rounded-3xl bg-teal/30 animate-pulse-ring" />
            <div className="relative flex h-40 w-40 flex-col items-center justify-center rounded-3xl bg-gradient-to-br from-navy-600 to-navy-800 ring-1 ring-cyan/40 shadow-[0_0_40px_-8px_rgba(0,240,255,0.55)]">
              <Accessibility className="h-14 w-14 text-cyan" aria-hidden="true" />
              <span className="mt-1.5 font-display text-xl font-bold tracking-[0.2em] text-white">A-ID</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export const Hero = () => {
  const sectionRef = useRef(null);
  const [activeDim, setActiveDim] = useState(0);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    const i = Math.min(DIMENSIONS.length - 1, Math.max(0, Math.floor(v * DIMENSIONS.length)));
    setActiveDim(i);
  });

  return (
    <section
      ref={sectionRef}
      id="top"
      className="relative min-h-screen grain overflow-hidden pt-28 pb-16 lg:pt-32 flex items-center"
      data-testid="hero-section"
    >
      {/* background layers */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-32 -left-24 h-[520px] w-[520px] rounded-full bg-teal/15 blur-[130px]" />
        <div className="absolute bottom-0 right-0 h-[480px] w-[480px] rounded-full bg-aigablue/20 blur-[140px]" />
        <div
          className="absolute inset-0 opacity-[0.4]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
            maskImage: "radial-gradient(circle at 50% 40%, black, transparent 78%)",
            WebkitMaskImage: "radial-gradient(circle at 50% 40%, black, transparent 78%)",
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid lg:grid-cols-2 gap-14 items-center">
        {/* left */}
        <div>
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border border-teal/30 bg-teal/5 px-4 py-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-teal">
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              Built by lived experience · Powered by technology
            </span>
          </Reveal>

          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.08] tracking-tight text-white">
            <RevealLines
              startDelay={0.15}
              lines={[
                <>Built by lived experience.</>,
                <span key="pt" className="text-gradient-teal">Powered by technology.</span>,
                <>Designed for independence.</>,
              ]}
            />
          </h1>

          <Reveal delay={0.5}>
            <p className="mt-6 max-w-xl text-lg text-slate-300 leading-relaxed">
              Aiga Enable is building technology that helps people communicate, manage and
              share their accessibility needs with greater confidence, choice and control.
            </p>
          </Reveal>

          {/* A-ID identity card */}
          <Reveal delay={0.62}>
            <div className="mt-8">
              <AidCard />
            </div>
          </Reveal>

          <Reveal delay={0.72}>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href="#a-id"
                onClick={(e) => handleAnchorClick(e, "#a-id")}
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-teal px-7 py-4 text-base font-semibold text-navy-900 hover:bg-cyan transition-colors shadow-[0_0_30px_-6px_rgba(0,240,255,0.6)]"
                data-testid="hero-discover-aid-btn"
              >
                Discover A-ID
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" aria-hidden="true" />
              </a>
              <a
                href="#contact"
                onClick={(e) => handleAnchorClick(e, "#contact")}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-7 py-4 text-base font-semibold text-white hover:bg-white/10 transition-colors"
                data-testid="hero-coming-soon-btn"
              >
                Coming Soon
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.8}>
            <p className="mt-6 inline-flex items-center gap-2 text-sm text-slatemuted">
              <ShieldCheck className="h-4 w-4 text-teal" aria-hidden="true" />
              Currently being developed and prepared for early testing.
            </p>
          </Reveal>
        </div>

        {/* right */}
        <Reveal delay={0.3} className="order-first lg:order-last">
          <HeroIdentityCard activeDim={activeDim} />
        </Reveal>
      </div>
    </section>
  );
};
